import { AdminLessonRoom } from './AdminLessonRoom';
import { ChangeTutorModal, canChangeTutor } from './ChangeTutorModal';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useAdminSession,
  useDeleteMessage,
  useForceCancelSession,
  useTranscript,
} from '../../api/admin';
import { Page, PageHeader } from '../../components/Layout';
import { Avatar } from '../../components/Avatar';
import { Badge, PresenceDot, SessionStatusBadge } from '../../components/Badge';
import { ConfirmDialog } from '../../components/Modal';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import {
  formatDateTime,
  formatDuration,
  formatRelative,
  formatSlotRange,
} from '../../lib/format';
import { ApiError } from '../../lib/api';
import type { SessionParticipant, TranscriptMessage } from '../../types/api';

function ParticipantCard({
  label,
  person,
}: {
  label: string;
  person: SessionParticipant;
}) {
  return (
    <div className="flex items-start gap-3">
      <Avatar name={person.fullName} src={person.avatarUrl} />
      <div className="min-w-0">
        <div className="text-[0.6875rem] text-ink-3">{label}</div>
        <Link
          to={`/users/${person.id}`}
          className="block truncate text-[0.8125rem] font-medium text-ink hover:text-accent hover:underline"
        >
          {person.fullName}
        </Link>
        <div className="mt-1 flex items-center gap-2">
          <PresenceDot online={person.isOnline} />
          <span className="text-[0.6875rem] text-ink-3">{person.timezone}</span>
        </div>
      </div>
    </div>
  );
}

function TranscriptRow({
  message,
  isTutor,
  onDelete,
}: {
  message: TranscriptMessage;
  isTutor: boolean;
  onDelete: () => void;
}) {
  return (
    <li className="group flex gap-3 px-4 py-2.5">
      <Avatar name={message.senderName} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-[0.8125rem] font-medium text-ink">
            {message.senderName}
          </span>
          <Badge>{isTutor ? 'tutor' : 'student'}</Badge>
          <span
            className="text-[0.6875rem] text-ink-3"
            title={formatDateTime(message.sentAt)}
          >
            {formatRelative(message.sentAt)}
          </span>
          {message.readAt && (
            <span className="text-[0.6875rem] text-ink-3">· read</span>
          )}
          {message.isDeleted && <Badge tone="danger">Removed</Badge>}
        </div>

        <p
          className={`mt-0.5 whitespace-pre-wrap break-words text-[0.8125rem] ${
            message.isDeleted ? 'text-ink-3 line-through' : 'text-ink-2'
          }`}
        >
          {message.body}
        </p>
        {message.isDeleted && (
          <p className="mt-0.5 text-[0.6875rem] text-ink-3">
            Hidden from both participants. Kept here for the record.
          </p>
        )}
      </div>

      {!message.isDeleted && (
        <button
          type="button"
          onClick={onDelete}
          className="btn btn-ghost btn-sm shrink-0 self-start opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
        >
          Remove
        </button>
      )}
    </li>
  );
}

export function SessionDetailPage() {
  const { id = '' } = useParams();
  const { notify } = useToast();
  const session = useAdminSession(id);
  const transcript = useTranscript(id);
  const forceCancel = useForceCancelSession(id);
  const deleteMessage = useDeleteMessage(id);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [changeTutorOpen, setChangeTutorOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [pendingDelete, setPendingDelete] = useState<TranscriptMessage | null>(null);

  if (session.isLoading) {
    return (
      <Page>
        <div className="flex h-64 items-center justify-center text-ink-3">
          <Spinner className="size-5" />
        </div>
      </Page>
    );
  }

  if (session.isError || !session.data) {
    return (
      <Page>
        <ErrorState
          title="Could not load this lesson"
          message={session.error instanceof ApiError ? session.error.message : undefined}
          onRetry={() => void session.refetch()}
        />
      </Page>
    );
  }

  const s = session.data;
  const canCancel = s.status === 'scheduled' || s.status === 'in_progress';

  const confirmCancel = async () => {
    try {
      await forceCancel.mutateAsync(reason.trim());
      notify('Lesson cancelled. Both people have been notified.', 'success');
      setReason('');
      setCancelOpen(false);
    } catch (err) {
      notify(
        err instanceof ApiError ? err.message : 'Could not cancel this lesson.',
        'error',
      );
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteMessage.mutateAsync(pendingDelete.id);
      notify('Message removed', 'success');
      setPendingDelete(null);
    } catch (err) {
      notify(
        err instanceof ApiError ? err.message : 'Could not remove this message.',
        'error',
      );
    }
  };

  return (
    <Page>
      <nav className="mb-3 text-xs text-ink-3">
        <Link to="/sessions" className="hover:text-accent hover:underline">
          Lessons
        </Link>
        <span className="mx-1.5">/</span>
        <span className="text-ink-2">
          {s.student.fullName} with {s.tutor.fullName}
        </span>
      </nav>

      <PageHeader
        title={`${s.student.fullName} with ${s.tutor.fullName}`}
        description={formatSlotRange(s.startTime, s.endTime)}
        actions={
          <>
            {canChangeTutor(s) && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setChangeTutorOpen(true)}
              >
                Change tutor
              </button>
            )}
            {canCancel && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => setCancelOpen(true)}
              >
                Cancel this lesson
              </button>
            )}
          </>
        }
      />

      <AdminLessonRoom key={s.id} session={s} />

      <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
        <div className="space-y-4">
          <div className="panel p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ink">Status</h2>
              <SessionStatusBadge status={s.status} />
            </div>

            <dl className="space-y-2.5 text-[0.8125rem]">
              <div className="flex justify-between gap-3">
                <dt className="text-ink-3">Scheduled</dt>
                <dd className="text-right text-ink">{formatSlotRange(s.startTime, s.endTime)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-ink-3">Length</dt>
                <dd className="text-ink">{formatDuration(s.startTime, s.endTime)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-ink-3">Joinable now</dt>
                <dd className={s.isJoinable ? 'text-live-text' : 'text-ink'}>
                  {s.isJoinable ? 'Yes — the room is open' : 'No'}
                </dd>
              </div>
              {s.startedAt && (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-3">Started</dt>
                  <dd className="text-ink">{formatDateTime(s.startedAt)}</dd>
                </div>
              )}
              {s.endedAt && (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-3">Ended</dt>
                  <dd className="text-ink">{formatDateTime(s.endedAt)}</dd>
                </div>
              )}
              <div className="flex justify-between gap-3">
                <dt className="text-ink-3">Booked</dt>
                <dd className="text-ink" title={formatDateTime(s.createdAt)}>
                  {formatRelative(s.createdAt)}
                </dd>
              </div>
            </dl>

            {s.cancelledAt && (
              <div className="mt-3 rounded-md border border-danger/30 bg-danger-wash px-3 py-2">
                <div className="text-xs font-medium text-ink">
                  Cancelled {formatRelative(s.cancelledAt)}
                </div>
                {s.cancellationReason && (
                  <p className="mt-0.5 text-xs text-ink-2">“{s.cancellationReason}”</p>
                )}
                {s.cancelledBy && (
                  <Link
                    to={`/users/${s.cancelledBy}`}
                    className="mt-1 inline-block text-[0.6875rem] text-accent hover:underline"
                  >
                    See who cancelled it
                  </Link>
                )}
              </div>
            )}

            <div className="mt-4 space-y-2.5 border-t border-rule pt-3">
              <div>
                <dt className="text-[0.6875rem] text-ink-3">Lesson ID</dt>
                <dd className="mono mt-0.5 break-all text-[0.6875rem] text-ink-2">{s.id}</dd>
              </div>
              <div>
                <dt className="text-[0.6875rem] text-ink-3">Video channel</dt>
                <dd className="mono mt-0.5 break-all text-[0.6875rem] text-ink-2">
                  {s.agoraChannelName}
                </dd>
              </div>
            </div>
          </div>

          <div className="panel space-y-4 p-4">
            <h2 className="text-sm font-semibold text-ink">Who</h2>
            <ParticipantCard label="Student" person={s.student} />
            <ParticipantCard label="Tutor" person={s.tutor} />
          </div>

          {(s.subject || s.studentNote) && (
            <div className="panel p-4">
              <h2 className="mb-2 text-sm font-semibold text-ink">What it is about</h2>
              {s.subject && <Badge tone="accent">{s.subject}</Badge>}
              {s.studentNote && (
                <p className="mt-2 whitespace-pre-wrap text-[0.8125rem] leading-relaxed text-ink-2">
                  {s.studentNote}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="panel flex min-h-[20rem] flex-col overflow-hidden">
          <div className="flex items-center justify-between border-b border-rule px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold text-ink">Chat transcript</h2>
              <p className="mt-0.5 text-xs text-ink-3">
                For moderation. Removed messages still show their original text
                here — you cannot judge a report without it.
              </p>
            </div>
            {transcript.data && (
              <span className="tabular shrink-0 text-xs text-ink-3">
                {transcript.data.length} messages
              </span>
            )}
          </div>

          {transcript.isLoading ? (
            <div className="flex flex-1 items-center justify-center text-ink-3">
              <Spinner />
            </div>
          ) : transcript.isError ? (
            <ErrorState
              message={
                transcript.error instanceof ApiError ? transcript.error.message : undefined
              }
              onRetry={() => void transcript.refetch()}
            />
          ) : transcript.data && transcript.data.length > 0 ? (
            <ul className="flex-1 divide-y divide-rule overflow-y-auto">
              {transcript.data.map((m) => (
                <TranscriptRow
                  key={m.id}
                  message={m}
                  isTutor={m.senderId === s.tutor.id}
                  onDelete={() => setPendingDelete(m)}
                />
              ))}
            </ul>
          ) : (
            <EmptyState
              title="No messages"
              description="Neither person has written anything in this lesson's thread."
            />
          )}
        </div>
      </div>

      <ConfirmDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={confirmCancel}
        danger
        pending={forceCancel.isPending}
        title="Cancel this lesson?"
        description="Both people are notified immediately and the time slot is freed for another booking. This cannot be undone."
        confirmLabel="Cancel lesson"
        reason={reason}
        onReasonChange={setReason}
        requireReason
        reasonLabel="Reason"
        reasonPlaceholder="Tutor reported ill — rebooked for next week"
      />

      {changeTutorOpen && (
        <ChangeTutorModal session={s} onClose={() => setChangeTutorOpen(false)} />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        danger
        pending={deleteMessage.isPending}
        title="Remove this message?"
        description={
          pendingDelete
            ? `“${pendingDelete.body.slice(0, 140)}${pendingDelete.body.length > 140 ? '…' : ''}” — both people stop seeing it. It stays in this transcript and in the audit log.`
            : ''
        }
        confirmLabel="Remove message"
      />
    </Page>
  );
}
